import type { ClientDeleteOneResponse, ClientPaginatedResponse } from "api-types/common/commonResponses";
import type { UserAdminRole } from "api-types/users/userAdminRole";
import type { UserSchemaUpdateOne } from "api-types/users/usersRequests";
import type { ClientReconcileUserResult, ClientUserHydrated } from "api-types/users/usersResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import { handleFetchError } from "common/utils/fetchHelpers";
import { deleteWithTimeout, getWithTimeout, postWithTimeout, putWithTimeout } from "lib/utils/fetchAuthHelpers";
import type { StorageResponse } from "./types";

export async function syncUser(
  email: string,
  firstName: string,
  lastName: string,
): StorageResponse<ClientUserHydrated, null> {
  try {
    const res = await postWithTimeout(`${WEB_API_BASE_PATH}/users`, { email, firstName, lastName });

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function syncInternalUserSuperAdminRole(email: string): StorageResponse<ClientUserHydrated, null> {
  try {
    const res = await putWithTimeout(`${WEB_API_BASE_PATH}/users/admin-role`, { email, adminRole: "SUPER_ADMIN" });

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function putUser(
  id: string,
  { firstName, lastName, phone, tempUserRole, featureFlags, studioPrefs, productEmailOptIn }: UserSchemaUpdateOne,
): StorageResponse<ClientUserHydrated, null> {
  try {
    const res = await putWithTimeout(`${WEB_API_BASE_PATH}/users/${id}`, {
      firstName,
      lastName,
      phone,
      tempUserRole,
      featureFlags,
      studioPrefs,
      productEmailOptIn,
    });
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientUserHydrated, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function updateUserAdminRole(
  email: string,
  adminRole: UserAdminRole,
): StorageResponse<ClientUserHydrated, null> {
  try {
    const res = await putWithTimeout(`${WEB_API_BASE_PATH}/users/admin-role`, { email, adminRole });

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientUserHydrated, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function reconcileUserWithCognito(
  userId: string,
  resendIfPending?: boolean,
): StorageResponse<ClientReconcileUserResult, null> {
  try {
    const url = resendIfPending
      ? `${WEB_API_BASE_PATH}/users/${userId}/reconcile?resendIfPending=true`
      : `${WEB_API_BASE_PATH}/users/${userId}/reconcile`;
    const res = await postWithTimeout(url, {});

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientReconcileUserResult, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function disableOrEnableUser(userId: string, disable: boolean): StorageResponse<ClientUserHydrated, null> {
  try {
    const res = await putWithTimeout(`${WEB_API_BASE_PATH}/users/${userId}/disable`, { disable });

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientUserHydrated, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function confirmUser(userId: string): StorageResponse<ClientUserHydrated, null> {
  try {
    const res = await putWithTimeout(`${WEB_API_BASE_PATH}/users/${userId}/confirm`, {});

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientUserHydrated, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function deleteUser(userId: string): StorageResponse<ClientDeleteOneResponse, null> {
  try {
    const res = await deleteWithTimeout(`${WEB_API_BASE_PATH}/users/${userId}`);

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientDeleteOneResponse, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function searchUsers(
  search: string,
  limit: number = 10,
): StorageResponse<ClientPaginatedResponse<ClientUserHydrated>, null> {
  try {
    const params = new URLSearchParams({ search, limit: String(limit), offset: "0" });
    const res = await getWithTimeout(`${WEB_API_BASE_PATH}/users?${params.toString()}`);

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientPaginatedResponse<ClientUserHydrated>, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}
